import { useState, useEffect, useCallback, useRef } from 'react'
import type { OrderDocument } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { Alert, ConfirmDialog, btnPrimary, btnSecondary, btnDanger, card, input, colors } from '../../components/ui'
import { useModal } from '../../hooks/use-modal'

interface OrderDocumentsProps {
  readonly orderId: string
}

const CATEGORIES = ['Requisition', 'Insurance', 'Consent', 'Clinical Notes', 'Lab Report', 'Other'] as const

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function OrderDocuments({ orderId }: OrderDocumentsProps) {
  const [documents, setDocuments] = useState<readonly OrderDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [category, setCategory] = useState('')
  const [notes, setNotes] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<OrderDocument | null>(null)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const deleteModal = useModal()

  const fetchDocuments = useCallback(async () => {
    try {
      const result = await api.get<readonly OrderDocument[]>(`/orders/${orderId}/documents`)
      setDocuments(result.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      if (category) form.append('category', category)
      if (notes) form.append('notes', notes)

      await api.postForm(`/orders/${orderId}/documents`, form)
      setCategory('')
      setNotes('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      await fetchDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = (doc: OrderDocument) => {
    window.open(`/api/orders/${orderId}/documents/${doc.id}/download`, '_blank')
  }

  const confirmDelete = (doc: OrderDocument) => {
    setDeleteTarget(doc)
    deleteModal.open()
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await api.delete(`/orders/${orderId}/documents/${deleteTarget.id}`)
      deleteModal.close()
      setDeleteTarget(null)
      await fetchDocuments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading documents...</div>

  return (
    <div style={{ ...card, marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Documents</h3>

      {error && <Alert type="error" message={error} />}

      {documents.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${colors.border}`, textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>File</th>
              <th style={{ padding: '0.5rem' }}>Category</th>
              <th style={{ padding: '0.5rem' }}>Size</th>
              <th style={{ padding: '0.5rem' }}>Uploaded By</th>
              <th style={{ padding: '0.5rem' }}>Date</th>
              <th style={{ padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => (
              <tr key={doc.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>
                  <div>{doc.originalFilename}</div>
                  {doc.notes && <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>{doc.notes}</div>}
                </td>
                <td style={{ padding: '0.5rem', color: colors.textMuted }}>{doc.category || '-'}</td>
                <td style={{ padding: '0.5rem', color: colors.textMuted }}>{formatFileSize(doc.fileSize)}</td>
                <td style={{ padding: '0.5rem', color: colors.textMuted }}>{doc.uploadedByName || '-'}</td>
                <td style={{ padding: '0.5rem', color: colors.textMuted }}>
                  {new Date(doc.createdAt).toLocaleDateString()}
                </td>
                <td style={{ padding: '0.5rem 0 0.5rem 0.5rem', textAlign: 'right' }}>
                  <button
                    onClick={() => handleDownload(doc)}
                    style={{ ...btnSecondary, padding: '0.25rem 0.5rem', fontSize: '0.75rem', marginRight: '0.25rem' }}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => confirmDelete(doc)}
                    style={{ ...btnDanger, padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {documents.length === 0 && (
        <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.875rem' }}>No documents attached.</p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
            Category
          </label>
          <select style={{ ...input, width: 'auto' }} value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">-- None --</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>
            Notes
          </label>
          <input
            style={input}
            placeholder="Optional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleUpload}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ ...btnPrimary, opacity: uploading ? 0.6 : 1 }}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.close()
          setDeleteTarget(null)
        }}
        onConfirm={handleDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.originalFilename}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
      />
    </div>
  )
}
